import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_IDS, WHATSAPP_URL, scrollToId } from "@/lib/constants";
import { LANGUAGES, useLanguage } from "@/lib/i18n";
import WhatsAppIcon from "@/components/WhatsAppIcon";

function LangToggle({ compact = false }) {
  const { lang, setLang, t } = useLanguage();
  return (
    <div
      className={`flex items-center rounded-full border border-[#E2DCD5] bg-white/60 backdrop-blur p-1 ${compact ? "self-start" : ""}`}
      role="group"
      aria-label={t.aria.language}
    >
      {LANGUAGES.map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-3 py-1 rounded-full font-mono-label text-[10px] font-bold tracking-[0.15em] uppercase transition-all duration-300 ${
            lang === l ? "bg-cypress text-linen shadow" : "text-clay hover:text-terracotta"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-linen/85 backdrop-blur-xl border-b border-[#E2DCD5]/70 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between">
        <button
          data-testid="header-logo-link"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-3 group"
          aria-label={t.aria.backToTop}
        >
          <img src="/logo.svg" alt="Logo Casa Paola Arcore" className="w-9 h-9 rounded-lg transition-transform duration-500 group-hover:-rotate-6" />
          <span className="font-serif text-lg sm:text-xl font-semibold tracking-tight text-ink">
            Casa Paola <span className="italic text-terracotta">Arcore</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Navigazione principale">
          {NAV_IDS.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="font-mono-label text-[11px] uppercase tracking-[0.22em] text-clay hover:text-terracotta transition-colors duration-300"
            >
              {t.nav[id]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangToggle />
          </div>
          <a
            data-testid="header-booking-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-whatsapp px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:brightness-95 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-whatsapp/30"
          >
            <WhatsAppIcon className="w-4 h-4" />
            {t.book}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E2DCD5] bg-white/60 backdrop-blur"
            aria-label={open ? t.aria.closeMenu : t.aria.openMenu}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden mx-4 mt-3 rounded-2xl bg-linen/95 backdrop-blur-xl border border-[#E2DCD5] shadow-xl shadow-ink/5 overflow-hidden"
            aria-label="Navigazione mobile"
          >
            {NAV_IDS.map((id) => (
              <button
                key={id}
                data-testid={`mobile-nav-link-${id}`}
                onClick={() => go(id)}
                className="block w-full text-left px-6 py-4 font-serif text-xl text-ink border-b border-[#E2DCD5]/60 last:border-0 hover:bg-sand/60 transition-colors"
              >
                {t.nav[id]}
              </button>
            ))}
            <div className="px-6 py-4 border-t border-[#E2DCD5]/60">
              <LangToggle compact />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
